import { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Link2, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import {
  useJiraConfig,
  useJiraIssues,
  useRefreshJiraIssuesScoped,
  useUnlinkJiraIssueScoped,
  type JiraParentType,
} from "@/hooks/useJira";
import { JiraCard, JiraTableHeader } from "./JiraCard";
import { JiraIssueDrawer } from "./JiraIssueDrawer";
import { JiraLinkDialog } from "./JiraLinkDialog";

interface Props {
  /** The id of the planner project this panel renders for. */
  projectId: string;
  /** Default 'project' — keeps existing call sites working without changes. */
  parentType?: JiraParentType;
}

/**
 * Jira panel for a project. Lists every linked issue, with a count
 * rollup in the header that mirrors the Portfolio badge so the same
 * shorthand (`3/5`) reads consistently across the app.
 *
 * The "Also linked to" chip on each card surfaces every OTHER planner
 * item the issue is linked to.
 */
export function JiraPanel({ projectId, parentType = "project" }: Props) {
  const config = useJiraConfig();
  const issues = useJiraIssues(parentType, projectId);
  const refresh = useRefreshJiraIssuesScoped(parentType, projectId);
  const unlink = useUnlinkJiraIssueScoped(parentType, projectId);
  const [showLink, setShowLink] = useState(false);
  // One drawer instance for the whole panel — parents and children both
  // open into it. Keeps state simple and avoids stacked overlays.
  // `scopeProjectId` is the project the drawer should use to fetch
  // detail: for project-side panels it's projectId; for initiative
  // panels with a rolled-up card it's the supporting project the card
  // was rolled up from; for direct initiative links it's null (the
  // drawer falls back to the unscoped detail endpoint).
  const [drawerCtx, setDrawerCtx] = useState<
    { key: string; scopeProjectId: string | null; canUnlink: boolean } | null
  >(null);

  if (config.isLoading) {
    return (
      <div className="flex items-center justify-center rounded-xl bg-surface p-8 ring-1 ring-line">
        <Loader2 className="h-5 w-5 animate-spin text-ink-subtle" />
      </div>
    );
  }

  if (!config.data?.enabled) {
    return (
      <div className="rounded-xl bg-surface p-6 ring-1 ring-line">
        <div className="flex items-start gap-3">
          <Link2 className="mt-0.5 h-5 w-5 text-ink-subtle" />
          <div className="text-sm text-ink-3">
            <p className="font-medium text-ink">Jira integration is not configured.</p>
            <p className="mt-1 text-xs text-ink-muted">
              Set <code className="rounded bg-raised px-1">JIRA_EMAIL</code> and{" "}
              <code className="rounded bg-raised px-1">JIRA_API_TOKEN</code> on the backend to enable linking.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const data = issues.data;
  const total = data?.total ?? 0;
  const done = data?.done ?? 0;

  // Unlink removes the planner link only — the Jira issue is
  // untouched. An issue that is ALSO a child of another linked issue
  // renders twice (once as its own link, once under its parent);
  // removing the redundant link is how that duplicate goes away
  // (Brett, ETE-150 / SSE-734).
  const handleUnlink = async (issueKey: string) => {
    if (
      !confirm(
        `Unlink ${issueKey} from this ${parentType}? The Jira issue itself is not affected.`,
      )
    )
      return;
    try {
      await unlink.mutateAsync(issueKey);
      toast.success(`Unlinked ${issueKey}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? "Unlink failed");
    }
  };

  const handleRefresh = async () => {
    try {
      await refresh.mutateAsync();
      toast.success("Refreshed from Jira");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? "Refresh failed");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="rounded-xl bg-surface ring-1 ring-line"
    >
      <div className="flex items-center justify-between border-b border-line-soft px-5 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-ink">Jira</h2>
          {total > 0 && (
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-medium tabular-nums",
                done === total
                  ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-500/30"
                  : done > 0
                    ? "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-1 ring-sky-200 dark:ring-sky-500/30"
                    : "bg-raised text-ink-3 ring-1 ring-line",
              )}
            >
              {done}/{total} done
            </span>
          )}
          {!config.data.write_enabled && (
            <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-muted">
              Read-only
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleRefresh}
            disabled={refresh.isPending}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-ink-3 hover:bg-raised disabled:opacity-50"
            title="Refresh from Jira"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", refresh.isPending && "animate-spin")} />
            Refresh
          </button>
          <button
            onClick={() => setShowLink(true)}
            className="inline-flex items-center gap-1 rounded-lg bg-brand-navy-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-navy-700"
          >
            <Link2 className="h-3.5 w-3.5" />
            Link issue
          </button>
        </div>
      </div>

      <div className="overflow-x-auto p-4">
        {issues.isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-ink-subtle" />
          </div>
        ) : issues.isError ? (
          <p className="text-sm text-rose-600 dark:text-rose-400">
            {(issues.error as any)?.response?.data?.detail ?? "Failed to load Jira issues."}
          </p>
        ) : total === 0 ? (
          <div className="rounded-lg border border-dashed border-line bg-canvas p-6 text-center">
            <p className="text-sm text-ink-3">No Jira issues linked yet.</p>
            <p className="mt-1 text-xs text-ink-muted">
              Paste a key like <code className="rounded bg-surface px-1 ring-1 ring-line">DEV-1234</code> to start tracking.
            </p>
          </div>
        ) : (
          <div>
            {/* Table header — shared grid template with the rows below
                so columns line up. The wrapper enables a horizontal
                scrollbar on narrow viewports rather than wrapping. */}
            <div className="min-w-[960px]">
              <JiraTableHeader />
              <div className="mt-1 space-y-1.5">
                {data?.issues.map((issue) => (
                  <JiraCard
                    key={issue.issue_key}
                    issue={issue}
                    parentType={parentType}
                    parentId={projectId}
                    onUnlink={handleUnlink}
                    unlinking={unlink.isPending && unlink.variables === issue.issue_key}
                    onOpen={(key) => {
                      // Pick the right scope for the drawer's detail
                      // fetch. Rolled-up cards (via_project_id set)
                      // open against their source project so the
                      // "Supports" banner shows where the link
                      // actually lives.
                      const opened = (data?.issues ?? [])
                        .flatMap((i) => [i, ...(i.children ?? [])])
                        .find((i) => i.issue_key === key);
                      const scopeProjectId =
                        opened?.via_project_id ??
                        (parentType === "project" ? projectId : null);
                      // A link to THIS parent exists only for top-level
                      // rows that weren't rolled up from elsewhere.
                      const linked = (data?.issues ?? []).some(
                        (i) => i.issue_key === key && !i.via_project_id,
                      );
                      setDrawerCtx({ key, scopeProjectId, canUnlink: linked });
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {data?.issues && data.issues.length > 0 && (
        <div className="flex justify-end border-t border-line-soft px-5 py-2 text-[11px] text-ink-subtle">
          <a
            href={`${config.data.base_url}/issues/?jql=${encodeURIComponent(
              "key in (" + data.issues.map((i) => i.issue_key).join(", ") + ")",
            )}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 hover:text-ink-3"
          >
            Open all in Jira <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}

      {showLink && (
        <JiraLinkDialog
          projectId={projectId}
          parentType={parentType}
          allowedKeys={config.data.project_keys}
          onClose={() => setShowLink(false)}
        />
      )}

      <JiraIssueDrawer
        projectId={drawerCtx?.scopeProjectId ?? null}
        issueKey={drawerCtx?.key ?? null}
        canUnlink={drawerCtx?.canUnlink ?? false}
        writeEnabled={config.data.write_enabled}
        onClose={() => setDrawerCtx(null)}
      />
    </motion.div>
  );
}
